// Designer for the content-editor panel (`transaction.content.editor_panel`):
// whether the entry screen shows it, its title and icon, whether the
// search-result preview shows, and WHICH checks run with WHICH limits.
//
// Until the designer touches the check list it follows the defaults of the
// profile's kind (CONTENT_PANEL_DEFAULTS); the first edit copies them into the
// model so the list is then fully the designer's. "Reset to defaults" removes
// the copy again. Evaluation lives in @twasta/page-runtime/txn (contentPanel).

import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, RotateCcw, Trash2 } from 'lucide-react'
import {
  CONTENT_CHECK_TYPES, CONTENT_PANEL_BASE, SEARCH_FIELDS, contentColumn, defaultContentChecks,
  minTextLength, resolveContentPanel,
  type ContentCheck, type ContentCheckType, type ContentEditorPanel, type ContentProfileLite,
} from '@twasta/page-runtime/txn'
import IconPicker from '@/components/common/IconPicker'
import type { TransactionModel } from './types'

const label: React.CSSProperties = {
  display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 2, marginTop: 8,
}
const input: React.CSSProperties = {
  width: '100%', padding: '5px 8px', background: 'var(--color-input-bg)', border: '1px solid var(--color-border-strong)',
  borderRadius: 4, color: 'var(--color-text)', fontSize: '0.8625rem',
}
const small: React.CSSProperties = { ...input, padding: '3px 6px', fontSize: '0.8125rem' }
const row: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: '0.8625rem', color: 'var(--color-text)',
}
const iconBtn: React.CSSProperties = {
  background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--color-text-secondary)',
  display: 'flex', alignItems: 'center',
}
const hint: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.4 }

function roleLabel(field: string): string {
  if (SEARCH_FIELDS[field]) return SEARCH_FIELDS[field]
  const f = field.replace(/^seo\./, 'SEO ').replace(/_/g, ' ')
  return f.charAt(0).toUpperCase() + f.slice(1)
}

function uniqueId(base: string, taken: Set<string>): string {
  let id = base
  for (let i = 2; taken.has(id); i++) id = `${base}_${i}`
  return id
}

export default function ContentPanelSettings({ model, onModelChange }: {
  model: TransactionModel
  onModelChange: (m: TransactionModel) => void
}) {
  const profile = model.content as ContentProfileLite
  const resolved = useMemo(() => resolveContentPanel(profile), [profile])
  const sm = model.sql_models?.[0]?.sql_model
  const [newType, setNewType] = useState<ContentCheckType>('filled')
  const [newField, setNewField] = useState('')

  // Fields a check may target: every mapped role, the mapped SEO keys and the
  // two search-result fields.
  const fieldOptions = useMemo(() => {
    const out: { value: string; text: string; column?: string }[] = []
    for (const f of Object.keys(SEARCH_FIELDS)) out.push({ value: f, text: SEARCH_FIELDS[f] })
    for (const [role, col] of Object.entries(profile.roles || {})) {
      if (col) out.push({ value: role, text: `${roleLabel(role)} (${col})`, column: col })
    }
    for (const [k, col] of Object.entries(profile.seo || {})) {
      if (col) out.push({ value: `seo.${k}`, text: `${roleLabel(`seo.${k}`)} (${col})`, column: col })
    }
    return out
  }, [profile])

  const writePanel = (patch: Partial<ContentEditorPanel> | null, opts: { dropChecks?: boolean } = {}) => {
    const m: TransactionModel = JSON.parse(JSON.stringify(model))
    const content = (m.content || {}) as ContentProfileLite
    const panel: ContentEditorPanel = { ...(content.editor_panel || {}), ...(patch || {}) }
    if (opts.dropChecks) delete panel.checks
    // Keep the model lean: a value equal to the base default is not stored.
    if (panel.enabled !== false) delete panel.enabled
    if (!panel.title?.trim() || panel.title.trim() === CONTENT_PANEL_BASE.title) delete panel.title
    if (!panel.icon?.trim() || panel.icon.trim() === CONTENT_PANEL_BASE.icon) delete panel.icon
    if (panel.show_preview !== false) delete panel.show_preview
    if (Object.keys(panel).length) content.editor_panel = panel
    else delete content.editor_panel
    m.content = content
    onModelChange(m)
  }

  // Any check edit works on a materialised copy of the current list.
  const writeChecks = (fn: (list: ContentCheck[]) => ContentCheck[]) => {
    const base = resolved.checks.map(c => ({ ...c }))
    writePanel({ checks: fn(base) })
  }
  const patchCheck = (i: number, patch: Partial<ContentCheck>) =>
    writeChecks(list => list.map((c, j) => {
      if (j !== i) return c
      const next = { ...c, ...patch }
      for (const k of Object.keys(patch) as (keyof ContentCheck)[]) {
        if (next[k] === undefined || next[k] === '') delete next[k]
      }
      return next
    }))
  const move = (i: number, d: -1 | 1) => writeChecks(list => {
    const j = i + d
    if (j < 0 || j >= list.length) return list
    const next = [...list]
    ;[next[i], next[j]] = [next[j], next[i]]
    return next
  })
  const addCheck = () => {
    const field = newField || fieldOptions[0]?.value
    if (!field) return
    writeChecks(list => {
      const taken = new Set(list.map(c => c.id))
      const c: ContentCheck = { id: uniqueId(`${field.replace(/\W+/g, '_')}_${newType}`, taken), type: newType, field }
      if (newType === 'words') c.min = 80
      return [...list, c]
    })
  }

  const numberOrUndef = (v: string): number | undefined => {
    const n = Math.trunc(Number(v))
    return v.trim() && Number.isFinite(n) && n >= 0 ? n : undefined
  }

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--color-border)' }}>
      <div style={{ fontSize: '0.8425rem', fontWeight: 600 }}>Entry-screen panel</div>
      <div style={hint}>Shown under the form while a record is written: a search-result preview and a “before you publish” checklist. Advisory — it never blocks a save.</div>

      <label style={row}>
        <input type="checkbox" checked={resolved.enabled}
          onChange={e => writePanel({ enabled: e.target.checked })} />
        <span>Show the panel on the entry screen</span>
      </label>

      {resolved.enabled && (
        <>
          <label style={label}>Title</label>
          <input style={input} value={profile.editor_panel?.title ?? ''} placeholder={CONTENT_PANEL_BASE.title}
            onChange={e => writePanel({ title: e.target.value })} />

          <label style={label}>Icon</label>
          <IconPicker value={resolved.icon} onChange={name => writePanel({ icon: name })} />

          <label style={row}>
            <input type="checkbox" checked={resolved.show_preview}
              onChange={e => writePanel({ show_preview: e.target.checked })} />
            <span>Show the search-result preview</span>
          </label>

          <div style={{ display: 'flex', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>Checks</span>
            <span style={{ ...hint, marginLeft: 6 }}>
              {resolved.customised ? 'customised' : `defaults for “${profile.kind || 'custom'}”`}
            </span>
            {resolved.customised && (
              <button type="button" style={{ ...iconBtn, marginLeft: 'auto', gap: 3, fontSize: '0.8125rem', color: 'var(--color-accent)' }}
                title={`Go back to the ${profile.kind || 'custom'} defaults`}
                onClick={() => writePanel(null, { dropChecks: true })}>
                <RotateCcw size={12} /> Reset to defaults
              </button>
            )}
          </div>

          {resolved.checks.length === 0 && <div style={{ ...hint, marginTop: 4 }}>No checks — only the preview shows.</div>}

          {resolved.checks.map((c, i) => {
            const info = CONTENT_CHECK_TYPES[c.type]
            const opt = fieldOptions.find(o => o.value === c.field)
            const colName = c.field === 'search.title' ? profile.roles?.title
              : c.field === 'search.description' ? (profile.seo?.description || profile.roles?.summary)
                : opt?.column
            const colMin = c.type === 'length' ? minTextLength(contentColumn(sm, colName)) : null
            const unmapped = !opt
            return (
              <div key={`${c.id}-${i}`} style={{
                marginTop: 6, padding: 8, borderRadius: 6, border: '1px solid var(--color-border)',
                background: 'var(--color-bg)', opacity: c.enabled === false ? 0.6 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={c.enabled !== false} title="Run this check"
                    onChange={e => patchCheck(i, { enabled: e.target.checked ? undefined : false })} />
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{info?.label || c.type}</span>
                  <span style={{ marginLeft: 'auto', display: 'flex' }}>
                    <button type="button" style={iconBtn} title="Move up" onClick={() => move(i, -1)}><ArrowUp size={12} /></button>
                    <button type="button" style={iconBtn} title="Move down" onClick={() => move(i, 1)}><ArrowDown size={12} /></button>
                    <button type="button" style={iconBtn} title="Remove check"
                      onClick={() => writeChecks(list => list.filter((_, j) => j !== i))}><Trash2 size={12} /></button>
                  </span>
                </div>
                <select style={{ ...small, marginTop: 6 }} value={c.field}
                  onChange={e => patchCheck(i, { field: e.target.value })}>
                  {unmapped && <option value={c.field}>{roleLabel(c.field)} — not mapped (skipped)</option>}
                  {fieldOptions.map(o => <option key={o.value} value={o.value}>{o.text}</option>)}
                </select>
                <input style={{ ...small, marginTop: 4 }} value={c.label ?? ''}
                  placeholder="Text shown (generated when blank) — {min} {max} {count}"
                  onChange={e => patchCheck(i, { label: e.target.value || undefined })} />
                {info && info.bounds.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    {info.bounds.includes('min') && (
                      <input type="number" min={0} style={small}
                        disabled={colMin != null}
                        title={colMin != null ? 'Set by the column’s Min Length' : undefined}
                        placeholder={colMin != null ? `${colMin} (column)` : (c.type === 'words' ? 'Min words' : 'Min chars')}
                        value={colMin != null ? '' : (c.min ?? '')}
                        onChange={e => patchCheck(i, { min: numberOrUndef(e.target.value) })} />
                    )}
                    {info.bounds.includes('max') && (
                      <input type="number" min={0} style={small} placeholder="Max chars"
                        value={c.max ?? ''}
                        onChange={e => patchCheck(i, { max: numberOrUndef(e.target.value) })} />
                    )}
                  </div>
                )}
                {colMin != null && (
                  <div style={{ ...hint, marginTop: 3 }}>Minimum comes from the column’s Min Length ({colMin}).</div>
                )}
              </div>
            )
          })}

          <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
            <select style={small} value={newType} onChange={e => setNewType(e.target.value as ContentCheckType)}
              title={CONTENT_CHECK_TYPES[newType].description}>
              {(Object.keys(CONTENT_CHECK_TYPES) as ContentCheckType[]).map(t => (
                <option key={t} value={t}>{CONTENT_CHECK_TYPES[t].label}</option>
              ))}
            </select>
            <select style={small} value={newField || fieldOptions[0]?.value || ''} onChange={e => setNewField(e.target.value)}>
              {fieldOptions.map(o => <option key={o.value} value={o.value}>{o.text}</option>)}
            </select>
            <button type="button" onClick={addCheck} title="Add check"
              style={{ ...iconBtn, border: '1px dashed var(--color-border)', borderRadius: 4, padding: '0 8px', color: 'var(--color-accent)' }}>
              <Plus size={14} />
            </button>
          </div>
          <div style={{ ...hint, marginTop: 4 }}>{CONTENT_CHECK_TYPES[newType].description}</div>
          {!resolved.customised && (
            <div style={{ ...hint, marginTop: 4 }}>
              Defaults of this kind: {defaultContentChecks(profile.kind).length} checks. Any change makes the list this screen’s own.
            </div>
          )}
        </>
      )}
    </div>
  )
}
